<template>
  <div id="previewRevision">
    <!-- Data Point Details -->
    <b-card
      id="revisionDetailsCard"
      class="blockDrawer large"
      :class="$store.state.drawer.previewRevision.position"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="hideDataPointDetails()"
        >
          <i class="far fa-times"></i>
        </a>
        <!-- Data Point Title -->
        <h4 class="text-warning">Data Point (Past Version)</h4>
        <!-- REVISIONS -->
        <b-btn
          v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor'"
          @click="promptConfirmRevert = true"
          class="mb-2 ml-1"
        >
           <i class="fas fa-history"></i> Revert
        </b-btn>
      </template>
      <div v-if="$store.state.dataset.dynamic">
        <!-- FIELDS -->
        <div
          v-for="(field, i) in $store.state.collector.fields"
          v-bind:key="i"
        >
          <p v-if="field.type === 'CV Number'"><strong>{{ field.label }}:</strong> {{ $store.state.revision[field.machine_name] }}</p>
          <div v-if="$store.state.revision[field.machine_name]">
            <!-- TEXT FIELDS -->
            <p
              v-if="field.type !== 'Image' && field.type !== 'Computer Vision' && field.type !== 'Multiselect' && field.type !== 'Formatted Text' && $store.state.revision[field.machine_name].length > 0"
            ><strong>{{ field.label }}:</strong> {{ $store.state.revision[field.machine_name] }}</p>

            <!-- FORMATTED TEXT FIELDS -->
            <p
              v-if="field.type === 'Formatted Text' && $store.state.revision[field.machine_name].length > 0"
              class="mb-0"
            ><strong>{{ field.label }}:</strong></p>
            <div
              v-if="field.type === 'Formatted Text' && $store.state.revision[field.machine_name].length > 0"
              v-html="sanitizeHtml($store.state.revision[field.machine_name])"
            ></div>

            <!-- MULTISELECT FIELD -->
            <p
              v-if="field.type === 'Multiselect' && $store.state.revision[field.machine_name].length > 0"
            >
              <strong>{{ field.label }}:</strong><br>
              <b-badge
                pill
                variant="dark"
                v-for="(val, i) in $store.state.revision[field.machine_name]"
                v-bind:key="i"
                class="mr-1"
              >{{ val }}</b-badge>
            </p>

            <!-- IMAGE GALLERY -->
            <b-carousel
              v-if="field.type === 'Image' && slideshowsReady && !fullscreenSlides && $store.state.revision[field.machine_name].length > 0"
              :id="field.machine_name"
              controls
              indicators
              class="mb-4"
            >
              <b-carousel-slide
                v-for="(image, i) in slideshows[field.machine_name].slides"
                v-bind:key="i"
                :img-src="image.url"
                :text="image.caption"
              ></b-carousel-slide>
              <b-btn
                @click="fullscreenSlides = true"
                class="fullscreenButton"
                variant="dark"
              ><i class="fas fa-expand"></i></b-btn>
            </b-carousel>
            <!-- FULL SCREEN GALLERY -->
            <div
              class="fullscreenSlideshow"
              v-if="field.type === 'Image' && slideshowsReady && fullscreenSlides"
            >
              <b-carousel
                :id="field.machine_name + '_fullscreen'"
                controls
                indicators
              >
                <b-carousel-slide
                  v-for="(image, i) in slideshows[field.machine_name].slides"
                  v-bind:key="i"
                  :img-src="image.url"
                  :text="image.caption"
                >
                  <b-btn
                    @click="downloadImage(image)"
                    class="downloadImageBtn"
                    variant="dark"
                  ><i class="fas fa-download"></i> Download Image</b-btn>
                </b-carousel-slide>
              </b-carousel>
              <b-btn
                @click="fullscreenSlides = false"
                class="closeButton"
                variant="dark"
              ><i class="fas fa-times"></i> Close</b-btn>
            </div><!-- /FULLSCREEN SLIDESHOW -->

            <!-- COMPUTER VISION PREVIEW -->
            <p
              v-if="field.type === 'Computer Vision' && slideshowsReady && !fullscreenSlides"
              class="mb-0"
            ><strong><i class="fas fa-robot mr-1"></i>{{ field.label }}:</strong></p>
            <div id="cvSection" v-if="field.type === 'Computer Vision' && slideshowsReady && !fullscreenSlides">
              <div
                v-for="(image, i) in slideshows[field.machine_name].slides"
                v-bind:key="i"
                class="preview-image-container"
                id="cvContainer"
              >
                <img :src="image.url" width="100%" id="cvImg">
              </div><!-- /.preview-image-container -->
            </div><!-- /#cvSection -->

          </div><!-- /.check for field data -->
        </div><!-- /FIELDS -->
        <p v-if="$store.state.revision.updatedOn != undefined"><strong>Updated:</strong> {{ $store.state.revision.updatedOn | dateTime }}</p>
        <p v-if="$store.state.revision.createdOn != undefined"><strong>Created:</strong> {{ $store.state.revision.createdOn | dateTime }}</p>
      </div><!-- if dynamic -->
      <div v-else>
        <p
          v-for="(field, i) in $store.state.revision.fields"
          v-bind:key="i"
        ><strong>{{ field }}:</strong> {{ $store.state.revision[field] }}</p>
      </div><!-- if dynamic -->
    </b-card>
    <Confirm
      v-if="promptConfirmRevert"
      text="Are you sure you want to revert to this version of the datapoint? A new revision will also be created using the current datapoint."
      v-on:confirmed="revert($event)"
    />
  </div><!-- /.previewRevision -->
</template>

<script>
import Confirm from '../Confirm'
import moment from 'moment'
import firebase from 'firebase/compat/app'
import DOMPurify from 'dompurify'
const fb = require('../../firebaseConfig.js')

export default {
  data () {
    return {
      slideshows: {},
      slideshowsReady: false,
      fullscreenSlides: false,
      promptConfirmRevert: false
    }
  },
  computed: {
    revision: {
      get () {
        return this.$store.state.revision
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'revision', value: value })
      }
    },
    dataPoint: {
      get () {
        return this.$store.state.dataPoint
      },
      set (value) {
        this.$store.commit('setDataPoint', value)
      }
    },
    collector: {
      get () {
        return this.$store.state.collector
      },
      set (value) {
        this.$store.commit('setCollector', value)
      }
    }
  },
  components: {
    Confirm
  },
  created () {
    // this.getImageUrls()
    this.formatFields()
  },
  methods: {
    revert (confirmed) {
      this.promptConfirmRevert = false
      if (confirmed) {
        console.log('Revert')
        console.log(this.revision)
        console.log(this.collector)
        console.log(this.dataPoint)
        // Create a revision with the current doc
        fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).get().then(dataPointDoc => {
          const dataPointData = dataPointDoc.data()
          
          // Create a clean copy for revision (without id field to avoid conflicts)
          const revisionData = { ...dataPointData }
          delete revisionData.id
          
          return fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).collection('revisions').add(revisionData)
        }).then(() => {
          this.revision.updatedOn = new Date()
          this.revision.updatedBy = this.$store.state.currentUser.uid
          this.revision.revisionMessage = 'Reverted to previous version: ' + this.revision.revisionMessage
          return fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).update(this.revision)
        }).then(() => {
          // Mark the collector with a new updatedOn date. Since editing something old, recompile as well
          return fb.collectorsCollection.doc(this.collector.id).update({
            updatedOn: new Date(),
            reCompile: true
          })
        }).then(() => {
          this.dataPoint = this.revision
          this.$store.commit('setAlert', { active: true, message: 'Data Point Updated! Recompiling map now...', level: 'alert-success' })
          this.$store.commit('hideDrawer', 'previewRevision')
          return this.$store.dispatch('removeAllLayers')
        }).then(() => {
          // setTimeout(() => {
          const dataset = this.$store.state.dataset
          return this.$store.dispatch('mapDataset', dataset)
          // }, 4000)
        }).then(() => {
          let newData = true
          const self = this
          fb.datasetsCollection.doc(self.$store.state.dataset.id).onSnapshot(snapshot => {
            const dataset = self.$store.state.dataset
            const datasetDoc = snapshot.data()
            datasetDoc.id = snapshot.id
            const filePath = dataset.file.geoJsonPath || ''
            const newFilePath = datasetDoc.file.geoJsonPath
            // Only rerender if new geoJson file
            if (filePath !== newFilePath && newData === true) {
              newData = false
              self.$store.commit('setDataset', datasetDoc)
              setTimeout(() => {
                self.$store.commit('setAlert', { active: false })
                self.$store.dispatch('removeAllLayers')
                self.$store.dispatch('mapDataset', datasetDoc)
              }, 1000)
            }
            // this.$store.commit('hideDrawer', 'collector')
          })
        }).catch(err => {
          console.log(err)
        })
      } else {
        console.log('Cancelled')
      }
    },
    downloadImage (image) {
      firebase.storage().ref(image.path).getDownloadURL().then(url => {
        window.open(url, '_blank')
      })
    },
    // getImageUrls () {
    //   this.$store.state.collector.fields.forEach(field => {
    //     if (this.$store.state.revision[field.machine_name] && field.type === 'Image') {
    //       this.slideshows[field.machine_name] = {
    //         slides: [],
    //         show: false
    //       }
    //       this.$store.state.revision[field.machine_name].forEach(image => {
    //         firebase.storage().ref(image.large).getDownloadURL().then(url => {
    //           this.slideshows[field.machine_name].slides.push({
    //             path: image.path,
    //             url: url,
    //             caption: image.caption
    //           })
    //           if (this.slideshows[field.machine_name].slides.length === this.$store.state.revision[field.machine_name].length) {
    //             this.slideshows[field.machine_name].show = true
    //             this.$store.state.collector.fields.forEach(field => {
    //               let readyToShowSlideshows = true
    //               if (field.type === 'Image' && !this.slideshows[field.machine_name].show) {
    //                 readyToShowSlideshows = false
    //               }
    //               this.slideshowsReady = readyToShowSlideshows
    //             })
    //           }
    //         })
    //       })
    //     } else if (this.$store.state.revision[field.machine_name] && field.type === 'Formatted Text') {
    //       const content = this.$store.state.revision[field.machine_name]
    //       const fixParenthesis = content.replace(/�s/g, '\'s')
    //       const removeSpecial = fixParenthesis.replace(/�/g, '"')
    //       this.$store.state.revision[field.machine_name] = removeSpecial
    //     }
    //   })
    // },
    getImageUrls () {
      this.$store.state.collector.fields.forEach(field => {
        if (field.type === 'Image') {
          this.slideshows[field.machine_name] = {
            slides: [],
            show: false
          }
          this.$store.state.revision[field.machine_name].forEach(image => {
            firebase.storage().ref(image.large).getDownloadURL().then(url => {
              this.slideshows[field.machine_name].slides.push({
                path: image.path,
                url: url
              })
              if (this.slideshows[field.machine_name].slides.length === this.$store.state.revision[field.machine_name].length) {
                this.slideshows[field.machine_name].show = true
                this.$store.state.collector.fields.forEach(field => {
                  let readyToShowSlideshows = true
                  if (field.type === 'Image' && !this.slideshows[field.machine_name].show) {
                    readyToShowSlideshows = false
                  }
                  this.slideshowsReady = readyToShowSlideshows
                })
              }
            })
          })
        }
      })
    },
    getImageUrl (image, field) {
      return firebase.storage().ref(image.large).getDownloadURL().then(url => {
        this.slideshows[field.machine_name].slides.push({
          path: image.path,
          url: url,
          caption: image.caption
        })
        return true
      }).catch(err => {
        console.log(err)
        return false
      })
    },
    drawPredictions (response, scaleX, scaleY) {
      this.classes = []
      this.classObjs = {}

      const container = document.getElementById('cvContainer')

      // SETUP LABELS AND COLORS
      const colors = ['red', 'yellow', 'orange', 'blue']
      const classLabels = []
      response.predictions.forEach(prediction => {
        if (!classLabels.includes(prediction.class)) {
          classLabels.push(prediction.class)
        }
      })
      classLabels.forEach((label, i) => {
        this.classes.push({
          label: label,
          color: colors[i]
        })
        this.classObjs[label] = {
          color: colors[i]
        }
      })

      response.predictions.forEach(prediction => {
        const div = document.createElement('div')
        div.className = 'prediction'
        div.style.width = `${prediction.width * scaleX}px`
        div.style.height = `${prediction.height * scaleY}px`
        div.style.left = `${prediction.x * scaleX}px`
        div.style.top = `${prediction.y * scaleY}px`
        div.style.borderColor = this.classObjs[prediction.class].color

        const text = document.createTextNode(`${prediction.confidence.toFixed(2)}`)
        div.appendChild(text)

        container.appendChild(div)
      })
    },
    formatFields () {
      this.$store.state.collector.fields.forEach(field => {
        if (this.$store.state.revision[field.machine_name] && (field.type === 'Image' || field.type === 'Computer Vision')) {
          this.slideshows[field.machine_name] = {
            slides: [],
            show: false
          }
          const imagePromises = []
          this.$store.state.revision[field.machine_name].forEach(image => {
            const prom = this.getImageUrl(image, field)
            imagePromises.push(prom)
          })
          Promise.all(imagePromises).then(() => {
            this.slideshowsReady = true
          })
        } else if (this.$store.state.revision[field.machine_name] && field.type === 'Formatted Text') {
          const content = this.$store.state.revision[field.machine_name]
          const fixParenthesis = content.replace(/�s/g, '\'s')
          const removeSpecial = fixParenthesis.replace(/�/g, '"')
          this.$store.state.revision[field.machine_name] = removeSpecial
        }
        if (this.$store.state.revision[field.machine_name] && field.type === 'Computer Vision') {
          setTimeout(() => {
            const img = document.getElementById('cvImg')
            const displayWidth = img.clientWidth
            const displayHeight = img.clientHeight
            const scaleX = displayWidth / img.naturalWidth
            const scaleY = displayHeight / img.naturalHeight
            console.log(this.$store.state.revision[field.machine_name])
            const dataPointCVData = this.$store.state.revision[field.machine_name]
            this.drawPredictions(dataPointCVData[0].cv, scaleX, scaleY)
          }, 1000)
        }
      })
    },
    hideDataPointDetails () {
      this.$store.commit('hideDrawer', 'previewRevision')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'revisions')
      }, 300)
    },
    // Sanitize Formatted Text revisions before v-html. The field
    // accepts rich text (Quill-style HTML) from any farm editor; a
    // malicious editor could otherwise inject <script>/<img onerror>
    // into a revision body and trigger stored XSS the moment another
    // viewer opens the revision drawer. DOMPurify in default config
    // strips scripts, event handlers, javascript: URLs, and the like
    // while leaving safe formatting (b/i/u, lists, links with safe
    // schemes, headings) intact.
    sanitizeHtml (html) {
      if (!html) return ''
      return DOMPurify.sanitize(String(html))
    }
  },
  filters: {
    dateTime (val) {
      if (!val) { return '-' }
      const date = val.toDate()
      return moment(date).fromNow()
    }
  }
}
</script>
